import React, { useCallback, useMemo } from "react";
import { useActionRequest } from "../../../../common/hooks/api";
import { NetworkRequestState } from "../../../../common/models";
import { Api } from "../../../../common/service/Api";
import {
  OrganizationDto,
  OrganizationMessageSettingsDto,
} from "../../../../common/service/server/dto/OrganizationDto";
import { SendMethod } from "../../../../common/service/server/Types";
import { Error as OrganizationError } from "../../../copy/organization";
import { usePrimaryOrganization, usePrimaryOrganizationId, useSelf } from "../../../hooks";
import { useQueryClient } from "@tanstack/react-query";
import { useOrganizationMailboxes } from "../../../hooks/useOrganization";
import { EmailAccountSettingRow } from "../../organisms/settings/rows/EmailAccountSettingRow";
import { EmailLogoSettingRow } from "../../organisms/settings/rows/EmailLogoSettingRow";
import { EmailSignatureSettingRow } from "../../organisms/settings/rows/EmailSignatureSettingRow";

interface EmailSettingsProps {
  mailbox: string;
  mailboxIsConnected: boolean;
  mailboxOauthUrl: string;
  testMessageRunning: boolean;
  messageSettings: OrganizationMessageSettingsDto;
  sendTestMessage: () => Promise<void>;
  deleteMailbox: () => void;
  updateMessageSettings: (values) => Promise<void>;
  updateSignature: (signature: string) => void;
  topDivider?: boolean;
  testEmailAddress?: string;
  sendMethod?: OrganizationDto["sendMethod"];
  updateSendMethod: (method: SendMethod) => Promise<void> | void;
  postmarkMailbox?: OrganizationDto["postmarkMailbox"];
}
export const EmailSettings: React.FC<EmailSettingsProps> = ({
  mailbox,
  mailboxIsConnected,
  mailboxOauthUrl,
  sendTestMessage,
  testMessageRunning,
  deleteMailbox,
  updateMessageSettings,
  messageSettings,
  updateSignature,
  sendMethod,
  updateSendMethod,
  postmarkMailbox,

  topDivider = false,
}) => {
  return (
    <div>
      {topDivider && <hr className="mb-lg" />}
      <EmailAccountSettingRow
        mailbox={mailbox}
        mailboxIsConnected={mailboxIsConnected}
        mailboxOauthUrl={mailboxOauthUrl}
        sendTestMessage={sendTestMessage}
        testMessageRunning={testMessageRunning}
        deleteMailbox={deleteMailbox}
        sendMethod={sendMethod}
        onSendMethodChange={updateSendMethod}
        postmarkMailbox={postmarkMailbox}
      />
      <hr className="my-lg" />
      <EmailLogoSettingRow
        headerImageUrl={messageSettings.headerImageUrl}
        updateMessageSettings={updateMessageSettings}
      />
      <hr className="my-lg" />
      <EmailSignatureSettingRow
        signature={messageSettings.messageSignature}
        defaultSignature={messageSettings.defaultMessageSignature}
        onSignatureChange={updateSignature}
      />
    </div>
  );
};

type UseEmailSettings = {
  settings: EmailSettingsProps;
  orgRequest: NetworkRequestState;
  mailboxesRequest: NetworkRequestState;
  userRequest: NetworkRequestState;
  organization: OrganizationDto;
  refreshMailboxes: () => void;
};
export const useEmailSettings = (): UseEmailSettings => {
  const organizationId = usePrimaryOrganizationId();
  const queryClient = useQueryClient();

  const [self, userRequest] = useSelf();

  const [organization, orgRequest, orgRefresh] = usePrimaryOrganization();
  const [mailboxes, mailboxesRequest, refreshMailboxes] = useOrganizationMailboxes(organizationId);

  const mailbox = useMemo(() => {
    if (!mailboxes || mailboxes?.length == 0) return null;
    let firstConnectedMailbox = mailboxes.find((m) => m.status == "CONNECTED");
    return firstConnectedMailbox || mailboxes[0];
  }, [mailboxes]);
  const updateSendMethodApi = useCallback(
    (method: SendMethod) =>
      Api.organization.updateSendMethod(organizationId, {
        sendMethod: method,
      }),
    [organizationId],
  );

  const { fetch: updateSendMethodRequest } = useActionRequest({
    api: updateSendMethodApi,
    messages: {
      forceError: OrganizationError.UpdateOrganization,
    },
    onSuccess: () => orgRefresh(),
  });
  const handleSendMethodChange = useCallback(
    (method: SendMethod) => updateSendMethodRequest(method),
    [updateSendMethodRequest],
  );

  const updateMessageApi = useCallback(
    (signature: string) => {
      return Api.organization.updateMessageSettings(
        organizationId,
        Object.assign({}, organization?.messageSettings, { messageSignature: signature }),
      );
    },
    [organizationId, organization],
  );

  const { fetch: updateMessageSignature } = useActionRequest({
    api: updateMessageApi,
    messages: {
      forceError: OrganizationError.UpdateOrganization,
    },
    onSuccess: () => orgRefresh(),
  });

  const sendTestMessageApi = useCallback(
    () => Api.organization.testMessage(organizationId),
    [organizationId],
  );

  const { fetch: sendTestMessage, request: testMessageRequest } = useActionRequest({
    api: sendTestMessageApi,
    onSuccess: async () => {
      await Promise.all([
        orgRefresh(),
        queryClient.invalidateQueries({ queryKey: ["postmarkMailbox", organizationId] }),
      ]);
    },
    messages: {
      defaultError:
        "Test email failed to send. Please check that your email account settings are correct and the mailbox is active.",
    },
  });

  const updateMessageSettingsApi = useCallback(
    ({ logoUrl }) =>
      Api.organization.updateMessageSettings(
        organizationId,
        Object.assign({}, organization?.messageSettings, { headerImageUrl: logoUrl }),
      ),
    [organizationId, organization],
  );

  const { fetch: updateMessageSettings } = useActionRequest({
    api: updateMessageSettingsApi,
    messages: {
      forceError: OrganizationError.UpdateOrganization,
    },
    onSuccess: () => orgRefresh(),
  });

  const updateSignature = (signature: string) => {
    updateMessageSignature(signature);
  };

  const deleteMailboxApi = useCallback(
    () => Api.organization.deleteOrganizationMailbox(organizationId, mailbox?.mailbox),
    [organizationId, mailbox?.mailbox],
  );

  const { fetch: deleteMailbox } = useActionRequest({
    api: deleteMailboxApi,
    onSuccess: () => {
      orgRefresh();
    },
  });

  const mailboxIsConnected = mailbox && organization?.mailbox.mailboxStatus === "OK";

  return {
    settings: {
      mailbox: mailbox?.mailbox,
      mailboxIsConnected: mailboxIsConnected,
      mailboxOauthUrl: organization?.mailbox.mailboxOauthUrl,
      messageSettings: organization?.messageSettings,
      testMessageRunning: testMessageRequest.running,
      sendTestMessage: sendTestMessage,
      deleteMailbox: deleteMailbox,
      updateMessageSettings: updateMessageSettings,
      updateSignature: updateSignature,
      testEmailAddress: self.email,
      sendMethod: organization?.sendMethod,
      updateSendMethod: handleSendMethodChange,
      postmarkMailbox: organization?.postmarkMailbox,
    },
    organization,
    orgRequest,
    mailboxesRequest,
    userRequest,
    refreshMailboxes,
  };
};
